import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";
import LoginPage from "./pages/login";
import SidebarLayout from "./components/layout/SidebarLayout";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { PermissionRoute } from "./components/auth/PermissionRoute";
import { MainAdminRoute } from "./components/auth/MainAdminRoute";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { NotFound } from "./components/ui/not-found";
import { Forbidden } from "./components/ui/forbidden";
import { Suspense, lazy } from "react";
import { LoadingPage } from "./components/ui/loading-page";
import './utils/debug-auth'; // Load debug utilities
import Subscriptions from "./pages/subscriptions";
import Users from "./pages/users";
import Enrollments from "./pages/enrollments";
import { Main } from "./pages/main";
import Payments from "./pages/payments";
import CheckoutTracking from "./pages/checkout-tracking";
import SubscriptionSetupPage from "./pages/subscription-setup";
import Programs from "./pages/programs";
import ProgramsV2 from "./pages/programs-v2";
import Certifications from "./pages/certifications";
import ProgramDrafts from "./pages/program-drafts";
import { TicketsPage } from "./pages/tickets";
import { QueriesPage } from "./pages/queries";
import FaqsPage from "./pages/faqs";
import ArticlesPage from "./pages/articles";
import CourseKeysPage from "./pages/course-keys";
import CourseKeyMindtapEnrollmentsRedirect from "./pages/course-key-mindtap-enrollments";
import QualificationFlowPage from "./pages/qualification-flow";
import FormsPage from "./pages/forms";
import FormDetailPage from "./pages/form-detail";
import FormEditPage from "./pages/form-edit";
import FormNewPage from "./pages/form-new";
import FormAppointmentAttendancePage from "./pages/form-appointment-attendance";
import FormSubmissionsPage from "./pages/form-submissions";
import InstructorAppointmentAttendancePage from "./pages/instructor-appointment-attendance";
import InstructorHomePage from "./pages/instructor-home";
import { InstructorShell } from "./components/layout/InstructorShell";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Lazy load pages that aren't implemented yet
const CreateProgramV2Step1 = lazy(() => import("./pages/create-program-v2-step1"));
const CreateProgramV2Step2 = lazy(() => import("./pages/create-program-v2-step2"));
import EditProgram from "./pages/edit-program";
import EditProgramV2 from "./pages/edit-program-v2";
const EditProgramV2Step1 = lazy(() => import("./pages/edit-program-v2-step1"));
const EditProgramV2Step2 = lazy(() => import("./pages/edit-program-v2-step2"));
const ProgramDetails = lazy(() => import("./pages/program-details"));
const EditDraft = lazy(() => import("./pages/edit-draft"));
const PreviewDraft = lazy(() => import("./pages/preview-draft"));
const Schedule = lazy(() => import("./pages/schedule"));

function LegacyCoursesRedirect() {
  return <Navigate to="/categories" replace />;
}

function LegacyCategoryProgramsRedirect() {
  const location = useLocation();
  const categoryId = new URLSearchParams(location.search).get("id");
  return (
    <Navigate
      to={
        categoryId
          ? `/programs-v2?categoryId=${encodeURIComponent(categoryId)}`
          : "/programs-v2"
      }
      replace
    />
  );
}
const Settings = lazy(() => import("./pages/settings"));
const Help = lazy(() => import("./pages/help"));
const Categories = lazy(() => import("./pages/categories"));
const StaffRolesPage = lazy(() => import("./pages/staff-roles"));
const UserDetails = lazy(() => import("./pages/user-details"));
const EmailTemplates = lazy(() => import("./pages/email/email-templates"));
const EmailTemplateEditor = lazy(() => import("./pages/email/email-template-editor"));
const EmailOpsBoard = lazy(() => import("./pages/email-ops/email-ops-board"));
const EmailOpsTemplateEditor = lazy(() => import("./pages/email-ops/email-ops-template-editor"));
const EmailOpsTriggerRules = lazy(() => import("./pages/email-ops/email-ops-trigger-rules"));
const EmailOpsSent = lazy(() => import("./pages/email-ops/email-ops-sent"));
const EmailOpsTokenLibrary = lazy(() => import("./pages/email-ops/email-ops-token-library"));
const TranscriptTemplatesListPage = lazy(() => import("./pages/transcript-templates"));
const TranscriptTemplateEditPage = lazy(() => import("./pages/transcript-template-edit"));
const TranscriptRequestsPage = lazy(() => import("./pages/transcript-requests"));
const TranscriptRequestDraftPage = lazy(() => import("./pages/transcript-request-draft"));
const CouponsPage = lazy(() => import("./pages/coupons"));
const NameChangeRequestsPage = lazy(() => import("./pages/name-change-requests"));
const NameChangeRequestDetailPage = lazy(() => import("./pages/name-change-request-detail"));
const EducationVerificationRequestsPage = lazy(() => import("./pages/education-verification-requests"));
const EducationVerificationRequestDetailPage = lazy(
  () => import("./pages/education-verification-request-detail")
);
const HomePageLayoutPage = lazy(() => import("./pages/home-page-layout"));
const BookstoreProductsPage = lazy(() => import("./pages/bookstore-products"));
const BookstoreProductEditPage = lazy(() => import("./pages/bookstore-product-edit"));
const BookstoreOrdersPage = lazy(() => import("./pages/bookstore-orders"));
const BookstoreOrderDetailPage = lazy(() => import("./pages/bookstore-order-detail"));
const BookstoreSettingsPage = lazy(() => import("./pages/bookstore-settings"));
const NotificationsPage = lazy(() => import("./pages/notifications"));
const TasksPage = lazy(() => import("./pages/tasks"));
const RemindersPage = lazy(() => import("./pages/reminders"));
const JourneyFlowsPage = lazy(() => import("./pages/journey-flows"));
const ClinicalsPage = lazy(() => import("./pages/clinicals"));
const LeadAutomationsPage = lazy(() => import("./pages/lead-automations"));
const LeadsPage = lazy(() => import("./pages/leads"));
//hi there
const App = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <NuqsAdapter>
          <ThemeProvider>
            <AuthProvider>
              <Toaster position="bottom-center" />
              <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/admin/login" element={<Navigate to="/login" replace />} />

              {/* Protected routes */}
              <Route element={<ProtectedRoute />}>
                <Route path="/instructor" element={<InstructorShell />}>
                  <Route index element={<InstructorHomePage />} />
                  <Route path="forms/:formId/attendance" element={<InstructorAppointmentAttendancePage />} />
                </Route>
                <Route element={<SidebarLayout />}>
                  <Route path="/" element={
                    <PermissionRoute permissions={['dashboard.analytics.view']}>
                      <Main />
                    </PermissionRoute>
                  } />
                  <Route
                    path="/notifications"
                    element={
                      <Suspense fallback={<LoadingPage message="Loading notifications..." />}>
                        <NotificationsPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/tasks"
                    element={
                      <PermissionRoute permissions={['admin.settings.view', 'dashboard.analytics.view']}>
                        <Suspense fallback={<LoadingPage message="Loading tasks..." />}>
                          <TasksPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/reminders"
                    element={
                      <PermissionRoute permissions={['admin.settings.view', 'dashboard.analytics.view']}>
                        <Suspense fallback={<LoadingPage message="Loading reminders..." />}>
                          <RemindersPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/journey-flows"
                    element={
                      <PermissionRoute permissions={['journeys.flows.view', 'journeys.review.view']}>
                        <Suspense fallback={<LoadingPage message="Loading journey flows..." />}>
                          <JourneyFlowsPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/clinicals"
                    element={
                      <PermissionRoute permissions={['journeys.review.view', 'certificates.main.view']}>
                        <Suspense fallback={<LoadingPage message="Loading clinicals..." />}>
                          <ClinicalsPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/leads"
                    element={
                      <PermissionRoute permissions={['leads.inbox.view']}>
                        <Suspense fallback={<LoadingPage message="Loading leads..." />}>
                          <LeadsPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/lead-automations"
                    element={
                      <PermissionRoute permissions={['leadAutomations.flows.view', 'leadAutomations.activity.view']}>
                        <Suspense fallback={<LoadingPage message="Loading lead automations..." />}>
                          <LeadAutomationsPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />

                  {/* Forbidden page */}
                  <Route path="/forbidden" element={<Forbidden />} />

                  {/* Programs routes */}
                  <Route path="/programs" element={
                    <PermissionRoute permissions={['programs.published.view', 'programs.draft.view']}>
                      <Programs />
                    </PermissionRoute>
                  } />
                  <Route path="/programs-v2" element={
                    <PermissionRoute permissions={['programs.published.view', 'programs.draft.view']}>
                      <ProgramsV2 />
                    </PermissionRoute>
                  } />
                  <Route
                    path="/programs/home-page-layout"
                    element={
                      <PermissionRoute permissions={['programs.published.edit', 'programs.draft.edit']}>
                        <Suspense fallback={<LoadingPage message="Loading home page layout..." />}>
                          <HomePageLayoutPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route path="/certifications" element={
                    <PermissionRoute permissions={['certificates.main.view']}>
                      <Certifications />
                    </PermissionRoute>
                  } />
                  <Route
                    path="/transcripts/templates"
                    element={
                      <PermissionRoute permissions={['transcripts.main.view']}>
                        <Suspense fallback={<LoadingPage message="Loading transcript templates..." />}>
                          <TranscriptTemplatesListPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/transcripts/templates/edit"
                    element={
                      <PermissionRoute permissions={['transcripts.main.view']}>
                        <Suspense fallback={<LoadingPage message="Loading transcript editor..." />}>
                          <TranscriptTemplateEditPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/transcripts/requests"
                    element={
                      <PermissionRoute permissions={['transcripts.main.view']}>
                        <Suspense fallback={<LoadingPage message="Loading transcript requests..." />}>
                          <TranscriptRequestsPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/transcripts/requests/:requestId"
                    element={
                      <PermissionRoute permissions={['transcripts.main.view']}>
                        <Suspense fallback={<LoadingPage message="Loading transcript draft..." />}>
                          <TranscriptRequestDraftPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/coupons"
                    element={
                      <PermissionRoute permissions={['transcripts.main.view']}>
                        <Suspense fallback={<LoadingPage message="Loading coupons..." />}>
                          <CouponsPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/name-change/requests"
                    element={
                      <PermissionRoute permissions={['transcripts.main.view']}>
                        <Suspense fallback={<LoadingPage message="Loading name change requests..." />}>
                          <NameChangeRequestsPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/name-change/requests/:requestId"
                    element={
                      <PermissionRoute permissions={['transcripts.main.view']}>
                        <Suspense fallback={<LoadingPage message="Loading name change request..." />}>
                          <NameChangeRequestDetailPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/education-verification/requests"
                    element={
                      <PermissionRoute permissions={['transcripts.main.view']}>
                        <Suspense fallback={<LoadingPage message="Loading education verification requests..." />}>
                          <EducationVerificationRequestsPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/education-verification/requests/:requestId"
                    element={
                      <PermissionRoute permissions={['transcripts.main.view']}>
                        <Suspense fallback={<LoadingPage message="Loading education verification request..." />}>
                          <EducationVerificationRequestDetailPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route path="/programs/drafts" element={
                    <PermissionRoute permissions={['programs.draft.view']}>
                      <ProgramDrafts />
                    </PermissionRoute>
                  } />
                  <Route
                    path="/programs-v2/create"
                    element={
                      <PermissionRoute permissions={['programs.published.create', 'programs.draft.create']}>
                        <Suspense
                          fallback={
                            <LoadingPage message="Loading program form..." />
                          }
                        >
                          <CreateProgramV2Step1 />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/programs-v2/create/types"
                    element={
                      <PermissionRoute permissions={['programs.published.create', 'programs.draft.create']}>
                        <Suspense
                          fallback={
                            <LoadingPage message="Loading program form..." />
                          }
                        >
                          <CreateProgramV2Step2 />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/programs/:id"
                    element={
                      <PermissionRoute permissions={['programs.published.view', 'programs.draft.view']}>
                        <Suspense
                          fallback={
                            <LoadingPage message="Loading program details..." />
                          }
                        >
                          <ProgramDetails />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/programs/:id/edit"
                    element={
                      <PermissionRoute permissions={['programs.published.edit', 'programs.draft.edit']}>
                        <EditProgram />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/programs-v2/:id/edit"
                    element={
                      <PermissionRoute permissions={['programs.published.edit', 'programs.draft.edit']}>
                        <EditProgramV2 />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/programs-v2/:id/edit/step1"
                    element={
                      <PermissionRoute permissions={['programs.published.edit', 'programs.draft.edit']}>
                        <Suspense
                          fallback={
                            <LoadingPage message="Loading edit form..." />
                          }
                        >
                          <EditProgramV2Step1 />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/programs-v2/:id/edit/step2"
                    element={
                      <PermissionRoute permissions={['programs.published.edit', 'programs.draft.edit']}>
                        <Suspense
                          fallback={
                            <LoadingPage message="Loading program types..." />
                          }
                        >
                          <EditProgramV2Step2 />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/programs/drafts/:draftId/edit"
                    element={
                      <PermissionRoute permissions={['programs.draft.edit']}>
                        <Suspense
                          fallback={
                            <LoadingPage message="Loading draft editor..." />
                          }
                        >
                          <EditDraft />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/programs/drafts/:draftId/preview"
                    element={
                      <PermissionRoute permissions={['programs.draft.view']}>
                        <Suspense
                          fallback={
                            <LoadingPage message="Loading draft preview..." />
                          }
                        >
                          <PreviewDraft />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />

                  {/* Lazy loaded routes with suspense */}
                  <Route
                    path="/courses"
                    element={
                      <PermissionRoute permissions={['programs.published.view']}>
                        <LegacyCoursesRedirect />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/category-programs"
                    element={
                      <PermissionRoute permissions={['categories.main.view']}>
                        <LegacyCategoryProgramsRedirect />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/schedule"
                    element={
                      <PermissionRoute permissions={['dashboard.analytics.view']}>
                        <Suspense
                          fallback={<LoadingPage message="Loading schedule..." />}
                        >
                          <Schedule />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <PermissionRoute permissions={['admin.settings.view']}>
                        <Suspense
                          fallback={<LoadingPage message="Loading settings..." />}
                        >
                          <Settings />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/settings/staff-roles"
                    element={
                      <PermissionRoute permissions={['admin.staff.view', 'admin.roles.view']}>
                        <Suspense
                          fallback={<LoadingPage message="Loading staff & roles..." />}
                        >
                          <StaffRolesPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/help"
                    element={
                      <Suspense
                        fallback={<LoadingPage message="Loading help..." />}
                      >
                        <Help />
                      </Suspense>
                    }
                  />
                  <Route path="/subscriptions" element={
                    <PermissionRoute permissions={['subscriptions.main.view']}>
                      <Subscriptions />
                    </PermissionRoute>
                  } />
                  <Route path="/enrollments" element={
                    <PermissionRoute permissions={['users.main.view']}>
                      <Enrollments />
                    </PermissionRoute>
                  } />
                  <Route path="/users" element={
                    <PermissionRoute permissions={['users.main.view']}>
                      <Users />
                    </PermissionRoute>
                  } />
                  <Route path="/users/:id" element={
                    <PermissionRoute permissions={['users.main.view']}>
                      <Suspense
                        fallback={<LoadingPage message="Loading user details..." />}
                      >
                        <UserDetails />
                      </Suspense>
                    </PermissionRoute>
                  } />
                  <Route path="/payments" element={
                    <PermissionRoute permissions={['payments.main.view', 'payments.authorization.view']}>
                      <Payments />
                    </PermissionRoute>
                  } />
                  <Route path="/payments/subscription-setup" element={
                    <PermissionRoute permissions={['payments.main.view', 'payments.authorization.view']}>
                      <SubscriptionSetupPage />
                    </PermissionRoute>
                  } />
                  <Route path="/payments/checkout-tracking" element={
                    <PermissionRoute permissions={['checkoutTracking.view']}>
                      <CheckoutTracking />
                    </PermissionRoute>
                  } />
                  <Route path="/tickets" element={
                    <PermissionRoute permissions={['supportTickets.main.view', 'supportTickets.chat.view']}>
                      <TicketsPage />
                    </PermissionRoute>
                  } />
                  <Route path="/queries" element={
                    <PermissionRoute permissions={['supportTickets.main.view', 'supportTickets.chat.view']}>
                      <QueriesPage />
                    </PermissionRoute>
                  } />
                  <Route path="/faqs/:faqId" element={
                    <PermissionRoute permissions={['supportTickets.main.view', 'supportTickets.chat.view']}>
                      <FaqsPage />
                    </PermissionRoute>
                  } />
                  <Route path="/faqs" element={
                    <PermissionRoute permissions={['supportTickets.main.view', 'supportTickets.chat.view']}>
                      <FaqsPage />
                    </PermissionRoute>
                  } />
                  <Route path="/articles/:articleId" element={
                    <PermissionRoute permissions={['supportTickets.main.view', 'supportTickets.chat.view', 'admin.settings.view']}>
                      <ArticlesPage />
                    </PermissionRoute>
                  } />
                  <Route path="/articles" element={
                    <PermissionRoute permissions={['supportTickets.main.view', 'supportTickets.chat.view', 'admin.settings.view']}>
                      <ArticlesPage />
                    </PermissionRoute>
                  } />
                  <Route path="/course-keys" element={
                    <PermissionRoute permissions={['courseKeys.main.view', 'programs.published.view']}>
                      <CourseKeysPage />
                    </PermissionRoute>
                  } />
                  <Route path="/course-keys/:configId/enrollments" element={
                    <PermissionRoute permissions={['courseKeys.main.view', 'programs.published.view']}>
                      <CourseKeyMindtapEnrollmentsRedirect />
                    </PermissionRoute>
                  } />
                  <Route path="/qualification-flow" element={
                    <PermissionRoute permissions={['supportTickets.main.view', 'supportTickets.chat.view']}>
                      <QualificationFlowPage />
                    </PermissionRoute>
                  } />
                  <Route path="/f" element={
                    <PermissionRoute permissions={['supportTickets.main.view', 'supportTickets.chat.view']}>
                      <FormsPage />
                    </PermissionRoute>
                  } />
                  <Route path="/f/new" element={
                    <PermissionRoute permissions={['supportTickets.main.view', 'supportTickets.chat.view']}>
                      <FormNewPage />
                    </PermissionRoute>
                  } />
                  <Route path="/f/:formId" element={
                    <PermissionRoute permissions={['supportTickets.main.view', 'supportTickets.chat.view']}>
                      <FormDetailPage />
                    </PermissionRoute>
                  } />
                  <Route path="/f/:formId/edit" element={
                    <PermissionRoute permissions={['supportTickets.main.view', 'supportTickets.chat.view']}>
                      <FormEditPage />
                    </PermissionRoute>
                  } />
                  <Route path="/f/:formId/attendance" element={
                    <PermissionRoute permissions={['supportTickets.main.view', 'supportTickets.chat.view']}>
                      <FormAppointmentAttendancePage />
                    </PermissionRoute>
                  } />
                  <Route path="/f/:formId/submissions" element={
                    <PermissionRoute permissions={['supportTickets.main.view', 'supportTickets.chat.view']}>
                      <FormSubmissionsPage />
                    </PermissionRoute>
                  } />

                  {/* User Payment Routes */}

                  <Route
                    path="/categories"
                    element={
                      <PermissionRoute permissions={['categories.main.view']}>
                        <Suspense
                          fallback={
                            <LoadingPage message="Loading categories..." />
                          }
                        >
                          <Categories />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />

                  {/* Book Store */}
                  <Route
                    path="/bookstore/products"
                    element={
                      <PermissionRoute permissions={['bookstore.products.view']}>
                        <Suspense fallback={<LoadingPage message="Loading products..." />}>
                          <BookstoreProductsPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/bookstore/products/:productId"
                    element={
                      <PermissionRoute permissions={['bookstore.products.view']}>
                        <Suspense fallback={<LoadingPage message="Loading product..." />}>
                          <BookstoreProductEditPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/bookstore/orders"
                    element={
                      <PermissionRoute permissions={['bookstore.orders.view']}>
                        <Suspense fallback={<LoadingPage message="Loading orders..." />}>
                          <BookstoreOrdersPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/bookstore/orders/:orderId"
                    element={
                      <PermissionRoute permissions={['bookstore.orders.view']}>
                        <Suspense fallback={<LoadingPage message="Loading order..." />}>
                          <BookstoreOrderDetailPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/bookstore/settings"
                    element={
                      <PermissionRoute permissions={['bookstore.settings.view']}>
                        <Suspense fallback={<LoadingPage message="Loading bookstore settings..." />}>
                          <BookstoreSettingsPage />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />

                  {/* Email (System) */}
                  <Route
                    path="/email/templates"
                    element={
                      <PermissionRoute permissions={['admin.settings.view']}>
                        <Suspense fallback={<LoadingPage message="Loading email templates..." />}>
                          <EmailTemplates />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/email/templates/new"
                    element={
                      <PermissionRoute permissions={['admin.settings.view']}>
                        <Suspense fallback={<LoadingPage message="Loading template editor..." />}>
                          <EmailTemplateEditor />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/email/templates/:id"
                    element={
                      <PermissionRoute permissions={['admin.settings.view']}>
                        <Suspense fallback={<LoadingPage message="Loading template editor..." />}>
                          <EmailTemplateEditor />
                        </Suspense>
                      </PermissionRoute>
                    }
                  />

                  {/* Email Management (main admin only) */}
                  <Route
                    path="/email-ops"
                    element={
                      <MainAdminRoute>
                        <Suspense fallback={<LoadingPage message="Loading Email Management..." />}>
                          <EmailOpsBoard />
                        </Suspense>
                      </MainAdminRoute>
                    }
                  />
                  <Route
                    path="/email-ops/templates/:id/edit"
                    element={
                      <MainAdminRoute>
                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                          <Suspense fallback={<LoadingPage message="Loading email editor..." />}>
                            <EmailOpsTemplateEditor />
                          </Suspense>
                        </div>
                      </MainAdminRoute>
                    }
                  />
                  <Route
                    path="/email-ops/trigger-rules"
                    element={
                      <MainAdminRoute>
                        <Suspense fallback={<LoadingPage message="Loading trigger rules..." />}>
                          <EmailOpsTriggerRules />
                        </Suspense>
                      </MainAdminRoute>
                    }
                  />
                  <Route
                    path="/email-ops/sent"
                    element={
                      <MainAdminRoute>
                        <Suspense fallback={<LoadingPage message="Loading emails sent..." />}>
                          <EmailOpsSent />
                        </Suspense>
                      </MainAdminRoute>
                    }
                  />
                  <Route
                    path="/email-ops/tokens"
                    element={
                      <MainAdminRoute>
                        <Suspense fallback={<LoadingPage message="Loading token library..." />}>
                          <EmailOpsTokenLibrary />
                        </Suspense>
                      </MainAdminRoute>
                    }
                  />
                  {/* 404 page for unknown routes within protected area */}
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Route>

              {/* Global 404 fallback for all other routes */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthProvider>
        </ThemeProvider>
        </NuqsAdapter>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
